import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import { ErrorApi } from '../api/errores'

/**
 * B.1 — Vista de calendario de una semana.
 *
 * Este hook es OPCIONAL a proposito: el componente `CalendarioSemana` es
 * presentacional y recibe los repartos por props, para que se pueda pintar y
 * probar sin red. El hook existe para el dia en que la pagina real lo enchufe a
 * la API, pero NO acopla el componente a un endpoint concreto: hoy el backend
 * de NestJS todavia no expone `/api/agendamiento/calendario` (esta en la Fase 2
 * del plan), y por eso el hook TOLERA que el endpoint no exista y devuelve una
 * semana vacia en vez de un error rojo en pantalla.
 */

/**
 * Los siete dias en orden de lectura. Se declara como tupla `as const` para que
 * el tipo `DiaSemana` salga de aqui y no haya dos listas que puedan divergir: la
 * cuadricula del calendario recorre exactamente este array.
 */
export const DIAS_SEMANA = [
  'lunes',
  'martes',
  'miercoles',
  'jueves',
  'viernes',
  'sabado',
  'domingo',
] as const

export type DiaSemana = (typeof DIAS_SEMANA)[number]

/** Etiquetas para pintar: el codigo interno va sin tildes, la pantalla con ellas. */
export const ETIQUETA_DIA: Record<DiaSemana, string> = {
  lunes: 'Lunes',
  martes: 'Martes',
  miercoles: 'Miércoles',
  jueves: 'Jueves',
  viernes: 'Viernes',
  sabado: 'Sábado',
  domingo: 'Domingo',
}

/**
 * Un reparto agendado dentro de un dia. Es el minimo que la vista necesita para
 * mostrar la carga de la semana de un vistazo; cuando exista el endpoint real
 * se ampliara desde `openapi.json` (via `npm run tipos`), no editando esto a
 * mano. Se define local y no en `tipos.ts` justamente porque el contrato aun no
 * esta cerrado en el backend.
 */
export interface RepartoDia {
  /** Identificador estable del reparto; sirve de `key` en la lista. */
  id: string
  /** Nombre del cliente al que se le reparte. Es lo unico imprescindible de leer. */
  cliente: string
  /**
   * Ruta de reparto (r1..r5), si se conoce. Opcional porque un cliente puede
   * tener varias rutas o ninguna, como ya documenta `/api/agendamiento/tabla`.
   */
  ruta?: string | null
  /** Que se le lleva, resumido. Opcional: en la vista semanal es un detalle. */
  resumen?: string | null
}

/**
 * Los repartos de la semana, agrupados por dia. Un `Record` completo (los siete
 * dias siempre presentes, con array vacio los que no tienen carga) en vez de un
 * array disperso: asi la cuadricula pinta las siete columnas sin comprobar si
 * falta la clave, y "dia sin repartos" y "dia sin cargar" no se confunden.
 */
export type SemanaRepartos = Record<DiaSemana, RepartoDia[]>

/** La semana vacia canonica: los siete dias con cero repartos. */
export function semanaVacia(): SemanaRepartos {
  // Se construye en tiempo de ejecucion (no una constante compartida) para que
  // cada consulta tenga su propio objeto y nadie mute la copia de otro.
  return DIAS_SEMANA.reduce((acc, dia) => {
    acc[dia] = []
    return acc
  }, {} as SemanaRepartos)
}

/** Ruta donde vivira el calendario cuando el backend lo exponga (Fase 2). */
const RUTA_CALENDARIO = '/api/agendamiento/calendario'

/**
 * Trae los repartos de una semana.
 *
 * @param semana Identificador de semana (p. ej. `2026-W03`). Opcional: sin el,
 *   el backend asume la semana activa, igual que hacen los filtros de logistica.
 *
 * Mientras el endpoint no exista, un 404 (o cualquier error de cliente 4xx) se
 * traduce a una semana vacia: la pantalla del calendario debe poder montarse y
 * verse aunque el dato todavia no llegue. Un 5xx si se deja subir como error,
 * porque eso ya no es "aun no existe" sino "el servidor esta roto", y ahi callar
 * enganaria al operador.
 */
export function useCalendarioSemana(semana?: string) {
  const api = useApi()

  return useQuery<SemanaRepartos, ErrorApi>({
    // `semana` en la clave: al cambiar de semana la clave cambia y TanStack
    // Query no sirve la anterior. `?? 'activa'` para que la clave sea estable
    // cuando no se pasa semana.
    queryKey: ['calendario-semana', semana ?? 'activa'],
    queryFn: async ({ signal }) => {
      try {
        const datos = await api.pedir<SemanaRepartos>(RUTA_CALENDARIO, {
          parametros: { semana },
          senal: signal,
        })
        // El backend podria mandar un objeto parcial; se completa con los dias
        // que falten para que la vista nunca reciba un dia `undefined`.
        return { ...semanaVacia(), ...datos }
      } catch (error) {
        // El endpoint todavia no existe: no es un fallo del panel, es que esta
        // parte del backend aun no esta. Semana vacia y a seguir.
        if (error instanceof ErrorApi && error.estado < 500) return semanaVacia()
        throw error
      }
    },
    // No reintentar un 404: no se va a arreglar solo y solo retrasaria el
    // vacio. Los 5xx los reintenta la politica global del QueryClient.
    retry: (intentos, error) => {
      if (error instanceof ErrorApi && error.estado < 500) return false
      return intentos < 2
    },
  })
}
